/**
 * Parent Training chatbot service
 * Stateless RAG chatbot - no sessions, no stored history
 */

import authService from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface ParentTrainingMessage {
  role: "user" | "assistant";
  content: string;
}

export interface RAGChunk {
  content: string;
  source: string;
  page_number?: number;
  score: number;
  chunk_id: string;
}

export interface ParentTrainingCompletionData {
  content: string;
  rag_chunks: RAGChunk[] | null;
  tokens: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
}

/**
 * Send a message to the Parent Training chatbot with streaming response
 */
export async function sendParentTrainingMessage(
  message: string,
  conversationHistory: ParentTrainingMessage[],
  onContent: (content: string) => void,
  onComplete: (data: ParentTrainingCompletionData) => void,
  onError: (error: string) => void,
  ragEnabled: boolean = true,
  ragNumChunks: number = 5,
  ragMinScore: number = 0.3
): Promise<void> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/parent-training/chat/stream`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          conversation_history: conversationHistory,
          rag_enabled: ragEnabled,
          rag_num_chunks: ragNumChunks,
          rag_min_score: ragMinScore,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      onError(error.detail || "Failed to send message");
      return;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      onError("Unable to read stream");
      return;
    }

    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = JSON.parse(line.substring(6));

          if (data.type === "content") {
            onContent(data.content);
          } else if (data.type === "done") {
            onComplete({
              content: data.content,
              rag_chunks: data.rag_chunks,
              tokens: data.tokens,
            });
          } else if (data.error) {
            onError(data.error);
            break;
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : "Unknown error");
  }
}
